/**
 * D-EZ-102 — THE NEWSSTAND: every edition of The Stock Car Dispatch the league
 * has produced, as back issues.
 *
 * One race edition per race run, a season-in-review edition for every settled
 * season, and a winter edition (retirements, the Hall of Fame, the awards,
 * sponsor news) once that season's winter has been run. The headline on each
 * back issue is the game's own, built from the race's facts — so the shelf
 * costs nothing to draw, and opening an edition prints the full article.
 */

import type { HistorySeason } from '../history/index.ts';
import { allRaces, careerMilestones, sortedSeasons } from '../history/index.ts';
import type { RaceResult } from '../race.ts';
import { surname } from '../paper/fallbackPaper.ts';
import { howItWasWon, victoryHeadline } from './raceStory.ts';
import { SEASON_RACES } from '../schedule.ts';

/** D-EZ-109 — 'wire': the season's weekly Wires, one tile per season. */
export type IssueKind = 'race' | 'season' | 'winter' | 'wire';

export interface BackIssue {
  key: string;
  kind: IssueKind;
  seasonNumber: number;
  /** For a race edition. */
  raceNumber?: number;
  /** "Race 14 · Food City 500", "Season 2 in review", "Winter after season 2". */
  label: string;
  /** Where the race was, or what the edition covers. */
  sub: string;
  headline: string;
}

export interface SeasonShelf {
  seasonNumber: number;
  issues: BackIssue[];
}

/** A race result whose tape may have been stripped by the archive (D-EZ-65). */
function safeHow(race: RaceResult) {
  try {
    return howItWasWon({ ...race, leadChanges: race.leadChanges ?? [], finishingOrder: race.finishingOrder ?? [] });
  } catch {
    return null;
  }
}

/**
 * Every back issue, newest season first; inside a season the winter edition,
 * then the season in review, then the season's Wires (D-EZ-109), then the
 * races newest first.
 */
export function newsstand(input: readonly HistorySeason[]): SeasonShelf[] {
  const seasons = sortedSeasons(input);
  const history = allRaces(seasons);
  const firstWins = new Set(
    careerMilestones(history)
      .filter((m) => m.kind === 'first-win')
      .map((m) => `${m.seasonNumber}|${m.raceNumber}|${m.driverId}`),
  );
  const nthWin = new Map<string, number>(
    careerMilestones(history)
      .filter((m) => m.kind === 'nth-win')
      .map((m) => [`${m.seasonNumber}|${m.raceNumber}|${m.driverId}`, m.n] as const),
  );
  const shelves: SeasonShelf[] = [];
  for (const s of [...seasons].reverse()) {
    const issues: BackIssue[] = [];
    const settled = s.complete !== false;
    const champ = s.season.championId ? s.season.finalStandings.byDriver[s.season.championId]?.name ?? null : null;
    if (settled && s.offseason) {
      const retired = s.offseason.retirements?.length ?? 0;
      issues.push({
        key: `w${s.seasonNumber}`,
        kind: 'winter',
        seasonNumber: s.seasonNumber,
        label: `Winter after season ${s.seasonNumber}`,
        sub: retired > 0 ? `${retired} retirement${retired === 1 ? '' : 's'}, the awards and the Hall of Fame` : 'The awards, the records and the Hall of Fame',
        headline: 'The off-season news',
      });
    }
    if (settled && s.season.races.length > 0) {
      issues.push({
        key: `s${s.seasonNumber}`,
        kind: 'season',
        seasonNumber: s.seasonNumber,
        label: `Season ${s.seasonNumber} in review`,
        sub: `${s.season.races.length} races`,
        headline: champ ? `${champ} takes the championship` : `Season ${s.seasonNumber} in review`,
      });
    }
    // D-EZ-109 — the weekly Wires: one between every pair of races run.
    const wires = s.season.races.filter((r) => r.raceNumber < SEASON_RACES).length;
    if (wires > 0) {
      issues.push({
        key: `wire${s.seasonNumber}`,
        kind: 'wire',
        seasonNumber: s.seasonNumber,
        label: `The Wire · season ${s.seasonNumber}`,
        sub: `${wires} weekly edition${wires === 1 ? '' : 's'} — quotes, power rankings, the line, injuries`,
        headline: 'The week’s news between races',
      });
    }
    for (const r of [...s.season.races].sort((a, b) => b.raceNumber - a.raceNumber)) {
      const winnerId = r.race.winnerId;
      const winner = r.race.finishingOrder?.find((f) => f.driverId === winnerId);
      const key = `${s.seasonNumber}|${r.raceNumber}|${winnerId}`;
      const headline = winner
        ? victoryHeadline({
            winnerName: winner.name,
            raceName: r.scheduled.raceName,
            trackName: r.scheduled.trackName,
            how: safeHow(r.race),
            started: r.startPositions[winner.driverId] ?? null,
            firstCareerWin: firstWins.has(key),
            winMilestone: nthWin.get(key) ?? null,
            bigOne: (r.race.events ?? []).some((e) => e.momentTag === 'wreck.big-one'),
            defendedRace: false,
          })
        : `${r.scheduled.raceName}: no finisher`;
      issues.push({
        key: `r${s.seasonNumber}-${r.raceNumber}`,
        kind: 'race',
        seasonNumber: s.seasonNumber,
        raceNumber: r.raceNumber,
        label: `Race ${r.raceNumber} · ${r.scheduled.raceName}`,
        sub: `${r.scheduled.trackName}${winner ? ` · won by ${surname(winner.name)}` : ''}`,
        headline,
      });
    }
    shelves.push({ seasonNumber: s.seasonNumber, issues });
  }
  return shelves;
}
